import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFlag, faFolder, faEraser, faComment, faArrowRight, faSpinner } from '@fortawesome/free-solid-svg-icons';
import questionFormatService from '../../services/questionFormatService';
import CleanAnswerOptions from './CleanAnswerOptions';
import QuestionRating from './QuestionRating';
import LabTable from './LabTable';
import AssetDisplay from './AssetDisplay';

/**
 * Component to display question content and handle answer submission
 */
const QuestionContent = ({
  currentQuestion,
  currentAnswers,
  selectedAnswer: initialSelectedAnswer,
  setSelectedAnswer: parentSetSelectedAnswer,
  isAnswerSubmitted: initialIsAnswerSubmitted,
  setIsAnswerSubmitted: parentSetIsAnswerSubmitted,
  isCorrect: initialIsCorrect,
  setIsCorrect: parentSetIsCorrect,
  isFlagged,
  isLocked = false,
  submitAnswer: parentSubmitAnswer,
  skipQuestion: parentSkipQuestion,
  goToNextQuestion,
  themeStyles,
  isDarkMode,
  showClues,
  toggleClues,
  onToggleFolderModal,
  // Question Notes props
  showNotes,
  handleToggleNotes,
  currentQuestionNote,
  handleNoteChange,
  handleNoteKeyDown,
  handleNoteSave,
  isLoadingNote,
  isSavingNote,
  hasUnsavedChanges,
  noteLastSaved,
  noteError,
  // Question Assets props
  currentQuestionAssets,
  isLoadingAssets,
  assetsError,
  children
}) => {
  const questionTextRef = useRef(null);
  const [highlightRanges, setHighlightRanges] = useState([]);
  const [plainTextContent, setPlainTextContent] = useState('');
  const [currentQuestionHtml, setCurrentQuestionHtml] = useState('');
  const [selectedAnswer, setSelectedAnswer] = useState(initialSelectedAnswer || null);
  const [isAnswerSubmitted, setIsAnswerSubmitted] = useState(initialIsAnswerSubmitted || false);
  const [isCorrect, setIsCorrect] = useState(initialIsCorrect || false);

  const handleSetSelectedAnswer = (answer) => {
    setSelectedAnswer(answer);
    if (parentSetSelectedAnswer) parentSetSelectedAnswer(answer);
  };

  const handleSetIsAnswerSubmitted = (value) => {
    setIsAnswerSubmitted(value);
    if (parentSetIsAnswerSubmitted) parentSetIsAnswerSubmitted(value);
  };

  const handleSetIsCorrect = (value) => {
    setIsCorrect(value);
    if (parentSetIsCorrect) parentSetIsCorrect(value);
  };

  useEffect(() => {
    if (currentQuestion) {
      const html = showClues
        ? (currentQuestion.formattedWithClues || questionFormatService.getQuestionWithClues(currentQuestion, true))
        : (currentQuestion.formattedWithoutClues || questionFormatService.getQuestionWithClues(currentQuestion, false));
      
      setCurrentQuestionHtml(html);
      
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = html;
      setPlainTextContent(tempDiv.innerText);
      setHighlightRanges([]);
    } else {
      setCurrentQuestionHtml('');
      setPlainTextContent('');
      setHighlightRanges([]);
    }
  }, [currentQuestion, showClues]);

  const handleMouseUp = useCallback(() => {
    const selection = window.getSelection();
    const selectedText = selection.toString();

    if (selectedText.length > 0 && questionTextRef.current && questionTextRef.current.contains(selection.anchorNode)) {
      const range = selection.getRangeAt(0);
      
      const preSelectionRange = range.cloneRange();
      preSelectionRange.selectNodeContents(questionTextRef.current);
      preSelectionRange.setEnd(range.startContainer, range.startOffset);
      const plainTextStart = preSelectionRange.toString().length;

      const plainTextEnd = plainTextStart + selectedText.length;

      setHighlightRanges(prevRanges => {
        const newSelectionRange = { start: plainTextStart, end: plainTextEnd };
        let isUnhighlightOperation = false;

        for (const existingRange of prevRanges) {
          if (newSelectionRange.start >= existingRange.start && newSelectionRange.end <= existingRange.end) {
            isUnhighlightOperation = true;
            break;
          }
        }

        let updatedRanges = [];
        if (isUnhighlightOperation) {
          for (const existingRange of prevRanges) {
            if (existingRange.end <= newSelectionRange.start || existingRange.start >= newSelectionRange.end) {
              updatedRanges.push(existingRange);
            } else {
              if (existingRange.start < newSelectionRange.start) {
                updatedRanges.push({ start: existingRange.start, end: newSelectionRange.start });
              }
              if (existingRange.end > newSelectionRange.end) {
                updatedRanges.push({ start: newSelectionRange.end, end: existingRange.end });
              }
            }
          }
        } else {
          updatedRanges.push(newSelectionRange);
          updatedRanges.push(...prevRanges);
        }

        updatedRanges.sort((a, b) => a.start - b.start);
        const merged = [];
        if (updatedRanges.length > 0) {
          let currentMerged = { ...updatedRanges[0] };
          for (let i = 1; i < updatedRanges.length; i++) {
            if (updatedRanges[i].start <= currentMerged.end) {
              currentMerged.end = Math.max(currentMerged.end, updatedRanges[i].end);
            } else {
              merged.push(currentMerged);
              currentMerged = { ...updatedRanges[i] };
            }
          }
          merged.push(currentMerged);
        }
        return merged;
      });
      selection.removeAllRanges();
    }
  }, []);

  const renderQuestionWithHighlights = useCallback((originalHtml, ranges) => {
    if (!originalHtml || !ranges || ranges.length === 0) return originalHtml;

    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = originalHtml;
    const walker = document.createTreeWalker(tempDiv, NodeFilter.SHOW_TEXT, null, false);

    let currentNode;
    let currentPlainTextOffset = 0;
    const nodesToProcess = [];

    while ((currentNode = walker.nextNode())) {
      nodesToProcess.push({
        node: currentNode,
        plainTextStart: currentPlainTextOffset,
        plainTextEnd: currentPlainTextOffset + currentNode.nodeValue.length
      });
      currentPlainTextOffset += currentNode.nodeValue.length;
    }

    for (let i = nodesToProcess.length - 1; i >= 0; i--) {
      const { node, plainTextStart, plainTextEnd } = nodesToProcess[i];
      const originalText = node.nodeValue;
      let lastSplitIndex = originalText.length;

      const overlappingRanges = ranges.filter(range => 
        Math.max(plainTextStart, range.start) < Math.min(plainTextEnd, range.end)
      ).sort((a, b) => b.start - a.start);

      for (const range of overlappingRanges) {
        const highlightStartInNode = Math.max(0, range.start - plainTextStart);
        const highlightEndInNode = Math.min(originalText.length, range.end - plainTextStart);

        if (highlightStartInNode < highlightEndInNode) {
          if (lastSplitIndex > highlightEndInNode) {
            const afterNode = document.createTextNode(originalText.substring(highlightEndInNode, lastSplitIndex));
            node.parentNode.insertBefore(afterNode, node.nextSibling);
          }

          const span = document.createElement('span');
          span.className = 'highlight-text';
          span.textContent = originalText.substring(highlightStartInNode, highlightEndInNode);
          node.parentNode.insertBefore(span, node.nextSibling);

          lastSplitIndex = highlightStartInNode;
        }
      }

      if (lastSplitIndex > 0) {
        const remainingTextNode = document.createTextNode(originalText.substring(0, lastSplitIndex));
        node.parentNode.insertBefore(remainingTextNode, node.nextSibling);
      }
      
      node.parentNode.removeChild(node);
    }

    return tempDiv.innerHTML;
  }, []);

  // Memoize the rendered HTML to prevent unnecessary re-renders
  const renderedQuestionHtml = useMemo(() => {
    return renderQuestionWithHighlights(currentQuestionHtml, highlightRanges);
  }, [currentQuestionHtml, highlightRanges, renderQuestionWithHighlights]);

  useEffect(() => {
    // console.log(`[QuestionContent] showClues prop changed to: ${showClues}. Question ID: ${currentQuestion?.id}`);
    if (currentQuestion) {
      const contentToUse = showClues 
        ? 'Highlighted clues version' 
        : 'Standard version without highlights';
      // console.log(`[QuestionContent] Using ${contentToUse} for question ${currentQuestion.id}`);
    }
  }, [showClues, currentQuestion]);

  const handleAnswerSubmitted = (response, correct, skipped = false) => {
    if (skipped && parentSkipQuestion) {
      parentSkipQuestion(response);
    } else if (!skipped && parentSubmitAnswer) {
      parentSubmitAnswer(response, correct);
    }
  };

  if (!currentQuestion) {
    return (
      <div style={{ textAlign: 'center', padding: '30px', color: themeStyles.text.secondary }}>
        Loading question...
      </div>
    );
  }

  return (
    <div className="question-content" style={{ 
      margin: '10px 0',
      display: 'flex',
      flexDirection: 'column',
  // Prevent this section from growing to fill the container height,
  // which pushes the bottom controls far away
  flex: '0 0 auto'
    }}>
      <div 
        ref={questionTextRef}
        className="question-text" 
        onMouseUp={handleMouseUp}
        style={{ 
          fontSize: '18px',
          lineHeight: '1.6',
          color: isDarkMode ? '#f1f5f9' : '#334155',
          marginBottom: '12px',
          overflow: 'auto',
          padding: '12px',
          backgroundColor: isDarkMode ? 'rgba(30, 41, 59, 0.7)' : 'rgba(248, 250, 252, 0.9)',
          borderRadius: '10px',
          border: isDarkMode ? '1px solid rgba(255, 255, 255, 0.08)' : '1px solid rgba(0, 0, 0, 0.05)',
          boxShadow: isDarkMode ? '0 2px 6px rgba(0, 0, 0, 0.1)' : '0 2px 6px rgba(0, 0, 0, 0.03)',
          transition: 'all 0.2s ease'
        }}>
        <div dangerouslySetInnerHTML={{ __html: renderedQuestionHtml }} />
      </div>

      {/* Asset Display - Shows images, videos, audio right after question text */}
      <AssetDisplay 
        assets={currentQuestionAssets}
        isLoading={isLoadingAssets}
        error={assetsError}
        isDarkMode={isDarkMode}
        themeStyles={themeStyles}
      />

      {(() => {
        if (currentQuestion.hasClues) return true;
        
        const questionText = currentQuestion.formattedWithClues || 
                           currentQuestion.bodyFormatted || 
                           currentQuestion.body || 
                           '';
        const extractedClues = questionFormatService.extractClues(questionText);
        return extractedClues.length > 0;
      })() && (
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '20px',
          marginTop: '-10px' 
        }}>
          <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
            <button
              onClick={() => {
                // console.log('[QuestionContent] Toggle clues button clicked, current state:', showClues);
                toggleClues();
              }}
              style={{
                background: showClues ? 
                  (isDarkMode ? 'rgba(59, 130, 246, 0.8)' : '#3b82f6') : 
                  (isDarkMode ? 'rgba(71, 85, 105, 0.4)' : '#f1f5f9'),
                color: showClues ? '#ffffff' : (isDarkMode ? '#e2e8f0' : '#334155'),
                border: isDarkMode ? '1px solid rgba(255, 255, 255, 0.1)' : 'none',
                borderRadius: '6px',
                padding: '8px 14px',
                fontSize: '14px',
                fontWeight: 500,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                transition: 'all 0.2s ease',
                boxShadow: isDarkMode ? '0 2px 5px rgba(0, 0, 0, 0.2)' : '0 2px 5px rgba(0, 0, 0, 0.05)',
                position: 'relative',
                outline: showClues ? '2px solid rgba(59, 130, 246, 0.5)' : 'none',
                outlineOffset: '2px'
              }}
              data-state={showClues ? 'active' : 'inactive'}
            >
              {showClues ? '✓ Hide Clues' : '⚪ Show Clues'}
            </button>

            {/* Question Notes Button */}
            <button
              onClick={handleToggleNotes}
              style={{
                background: showNotes ? 
                  (isDarkMode ? 'rgba(34, 197, 94, 0.8)' : '#22c55e') : 
                  (isDarkMode ? 'rgba(71, 85, 105, 0.4)' : '#f1f5f9'),
                color: showNotes ? '#ffffff' : (isDarkMode ? '#e2e8f0' : '#334155'),
                border: isDarkMode ? '1px solid rgba(255, 255, 255, 0.1)' : 'none',
                borderRadius: '6px',
                padding: '8px 14px',
                fontSize: '14px',
                fontWeight: 500,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                transition: 'all 0.2s ease',
                boxShadow: isDarkMode ? '0 2px 5px rgba(0, 0, 0, 0.2)' : '0 2px 5px rgba(0, 0, 0, 0.05)',
                position: 'relative',
                outline: showNotes ? '2px solid rgba(34, 197, 94, 0.5)' : 'none',
                outlineOffset: '2px'
              }}
              data-state={showNotes ? 'active' : 'inactive'}
            >
              <FontAwesomeIcon 
                icon={faComment} 
                style={{ 
                  fontSize: '12px'
                }} 
              />
              {showNotes ? 'Hide Notes' : 'Question Notes'}
              {currentQuestionNote && !showNotes && (
                <span style={{
                  fontSize: '10px',
                  backgroundColor: isDarkMode ? 'rgba(34, 197, 94, 0.2)' : 'rgba(34, 197, 94, 0.1)',
                  color: isDarkMode ? '#22c55e' : '#16a34a',
                  padding: '2px 6px',
                  borderRadius: '10px',
                  marginLeft: '4px'
                }}>
                  {hasUnsavedChanges ? '!' : '✓'}
                </span>
              )}
            </button>
          </div>

          <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
            {highlightRanges.length > 0 && (
              <button
                onClick={() => setHighlightRanges([])}
                style={{
                  background: isDarkMode ? 'rgba(219, 214, 55, 0.8)' : '#ef4444',
                  color: '#ffffff',
                  border: 'none',
                  borderRadius: '6px',
                  padding: '8px 14px',
                  fontSize: '14px',
                  fontWeight: 500,
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  boxShadow: isDarkMode ? '0 2px 5px rgba(0, 0, 0, 0.2)' : '0 2px 5px rgba(0, 0, 0, 0.05)'
                }}
                title="Clear highlights"
              >
                <FontAwesomeIcon icon={faEraser} />
              </button>
            )}

            {/* Question Rating - Moved to the same line */}
            <QuestionRating
              questionId={currentQuestion?.id}
              isDarkMode={isDarkMode}
              themeStyles={themeStyles}
            />
          </div>
        </div>
      )}

      {/* Question Notes Content - Expandable */}
      {showNotes && currentQuestion && (
        <div style={{
          marginTop: '15px',
          marginBottom: '20px',
          padding: '15px',
          backgroundColor: isDarkMode ? 'rgba(30, 41, 59, 0.5)' : 'rgba(248, 250, 252, 0.8)',
          borderRadius: '8px',
          border: isDarkMode ? '1px solid rgba(255, 255, 255, 0.1)' : '1px solid rgba(0, 0, 0, 0.05)'
        }}>
          {isLoadingNote ? (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '20px',
              color: isDarkMode ? '#94a3b8' : '#64748b'
            }}>
              <FontAwesomeIcon icon={faSpinner} spin style={{ marginRight: '8px' }} />
              Loading note...
            </div>
          ) : (
            <>
              <div style={{ marginBottom: '12px' }}>
                <label style={{
                  display: 'block',
                  fontSize: '13px',
                  fontWeight: '500',
                  color: isDarkMode ? '#e2e8f0' : '#374151',
                  marginBottom: '6px'
                }}>
                  Your Notes for this Question:
                </label>
                <textarea
                  value={currentQuestionNote || ''}
                  onChange={handleNoteChange}
                  onKeyDown={handleNoteKeyDown}
                  placeholder="Add your notes about this question here... (Auto-saves while you type, or press Ctrl+S to save manually)"
                  style={{
                    width: '100%',
                    minHeight: '100px',
                    padding: '10px',
                    border: isDarkMode ? '1px solid rgba(255, 255, 255, 0.2)' : '1px solid rgba(0, 0, 0, 0.1)',
                    borderRadius: '6px',
                    backgroundColor: isDarkMode ? 'rgba(0, 0, 0, 0.2)' : '#ffffff',
                    color: isDarkMode ? '#e2e8f0' : '#374151',
                    fontSize: '14px',
                    fontFamily: 'inherit',
                    resize: 'vertical',
                    outline: 'none',
                    transition: 'border-color 0.2s ease'
                  }}
                  maxLength={2000}
                />
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  fontSize: '11px',
                  color: isDarkMode ? '#94a3b8' : '#6b7280',
                  marginTop: '4px'
                }}>
                  <div>
                    {hasUnsavedChanges ? (
                      <span style={{ color: isDarkMode ? '#fbbf24' : '#f59e0b' }}>
                        Unsaved changes (auto-saving...)
                      </span>
                    ) : noteLastSaved ? (
                      <span style={{ color: isDarkMode ? '#10b981' : '#059669' }}>
                        Saved {noteLastSaved.toLocaleTimeString()}
                      </span>
                    ) : (
                      <span>Auto-saves as you type</span>
                    )}
                  </div>
                  <div>
                    {(currentQuestionNote || '').length}/2000 characters
                  </div>
                </div>
              </div>

              {/* Save Button and Status */}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                flexWrap: 'wrap',
                gap: '10px'
              }}>
                <button
                  onClick={handleNoteSave}
                  disabled={isSavingNote || !hasUnsavedChanges}
                  style={{
                    padding: '8px 16px',
                    borderRadius: '6px',
                    border: 'none',
                    cursor: (!isSavingNote && hasUnsavedChanges) ? 'pointer' : 'not-allowed',
                    fontSize: '13px',
                    fontWeight: 500,
                    transition: 'all 0.2s ease',
                    backgroundColor: (!isSavingNote && hasUnsavedChanges) ? 
                      (isDarkMode ? '#3b82f6' : '#3b82f6') : 
                      (isDarkMode ? 'rgba(148, 163, 184, 0.2)' : 'rgba(148, 163, 184, 0.2)'),
                    color: (!isSavingNote && hasUnsavedChanges) ? 
                      '#ffffff' : 
                      (isDarkMode ? '#94a3b8' : '#64748b'),
                    opacity: (!isSavingNote && hasUnsavedChanges) ? 1 : 0.6
                  }}
                  title={hasUnsavedChanges ? "Save changes now (Ctrl+S)" : "No unsaved changes"}
                >
                  {isSavingNote && <FontAwesomeIcon icon={faSpinner} spin style={{ marginRight: '6px' }} />}
                  {isSavingNote ? 'Saving...' : hasUnsavedChanges ? 'Save Now' : 'Saved'}
                </button>

                {noteError && (
                  <div style={{
                    color: isDarkMode ? '#ef4444' : '#dc2626',
                    fontSize: '12px',
                    padding: '4px 8px',
                    backgroundColor: isDarkMode ? 'rgba(239, 68, 68, 0.1)' : 'rgba(220, 38, 38, 0.1)',
                    borderRadius: '4px'
                  }}>
                    {noteError}
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      )}

      <LabTable
        currentQuestion={currentQuestion}
        themeStyles={themeStyles}
        isDarkMode={isDarkMode}
      />

      {children || (
        <CleanAnswerOptions
          currentQuestion={currentQuestion}
          selectedAnswer={selectedAnswer}
          setSelectedAnswer={handleSetSelectedAnswer}
          isAnswerSubmitted={isAnswerSubmitted}
          setIsAnswerSubmitted={handleSetIsAnswerSubmitted}
          isCorrect={isCorrect}
          setIsCorrect={handleSetIsCorrect}
          isFlagged={isFlagged}
          isLocked={isLocked}
          themeStyles={themeStyles}
          isDarkMode={isDarkMode}
          onAnswerSubmitted={handleAnswerSubmitted}
        />
      )}
    </div>
  );
};

export default React.memo(QuestionContent);
